// StaffPermissionsTab.tsx — Admin Staff & RBAC tab (#235)
//
// Three sections:
//   1. Staff Roster      — all users with role admin or super_admin
//   2. Promote User      — search existing user by email, promote to admin
//   3. Audit Log         — recent admin_user_actions entries
//   4. RBAC Reference    — static permission matrix
//
// All write ops require super_admin. Reads require admin or super_admin.
// All operations are reason-gated and audit-logged (backend enforces this).
//
// Backend: reuses existing my-travel-agent-admin-users Lambda endpoints.
//   GET  /admin/users?page=N          — full user list (filter client-side for staff)
//   POST /admin/users/search          — search by email/name (reason required)
//   PATCH /admin/users/{id}           — role update (super_admin only)
//   GET  /admin/users/{id}/activity   — recent admin actions (audit log)

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Alert, Card, CardContent, Chip, Button,
    CircularProgress, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField, Select, MenuItem, FormControl, InputLabel,
    Table, TableBody, TableRow, TableCell, TableHead, Tooltip,
} from '@mui/material';
import {
    AdminPanelSettings, Search, Edit, CheckCircle, PersonAdd,
    History, Security, Info,
} from '@mui/icons-material';
import { api } from '../../utils/api';
import { PERMISSIONS, roleLabel, roleBadgeColor } from '../../utils/adminRbac';

const TEAL = '#2D6E6A';

// ── Types ─────────────────────────────────────────────────────────────────────

interface StaffMember {
    userId: string;
    email: string;
    displayName: string;
    username: string;
    role: 'admin' | 'super_admin';
    isActive: boolean;
    createdAt: string;
    lastLogin: string | null;
}

interface AuditEntry {
    action_type: string;
    action_details: any;
    reason: string;
    performed_at: string;
}

interface SearchResult {
    userId: string;
    email: string;
    displayName: string;
    username: string;
    role: string;
    isActive: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────


// ── Main Component ────────────────────────────────────────────────────────────

const StaffPermissionsTab: React.FC = () => {

    // ── Staff roster state ────────────────────────────────────────────────────
    const [staff, setStaff] = useState<StaffMember[]>([]);
    const [staffLoading, setStaffLoading] = useState(false);
    const [staffError, setStaffError] = useState('');

    // ── Promote user state ────────────────────────────────────────────────────
    const [promoteQuery, setPromoteQuery] = useState('');
    const [promoteReason, setPromoteReason] = useState('');
    const [promoteResults, setPromoteResults] = useState<SearchResult[]>([]);
    const [promoteSearchLoading, setPromoteSearchLoading] = useState(false);
    const [promoteSearchError, setPromoteSearchError] = useState('');
    const [hasPromoteSearched, setHasPromoteSearched] = useState(false);

    // ── Edit role dialog ──────────────────────────────────────────────────────
    const [editTarget, setEditTarget] = useState<StaffMember | SearchResult | null>(null);
    const [editRole, setEditRole] = useState('');
    const [editReason, setEditReason] = useState('');
    const [editLoading, setEditLoading] = useState(false);
    const [editError, setEditError] = useState('');
    const [editSuccess, setEditSuccess] = useState('');

    // ── Audit log state ───────────────────────────────────────────────────────
    const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
    const [auditLoading, setAuditLoading] = useState(false);
    const [auditError, setAuditError] = useState('');
    const [showAudit, setShowAudit] = useState(false);

    // ── Load staff roster ─────────────────────────────────────────────────────
    const loadStaff = useCallback(async () => {
        setStaffLoading(true);
        setStaffError('');
        try {
            // Load all users and filter to admin/super_admin client-side.
            // Using page=1 with limit 25 — staff count is always small.
            // If org ever has >25 admins, paginate here.
            const res = await api.get('/admin/users?page=1');
            const data = await res.json();
            if (!res.ok) {
                setStaffError(data.error || 'Failed to load staff');
                return;
            }
            const staffOnly = (data.users || []).filter(
                (u: any) => u.role === 'admin' || u.role === 'super_admin'
            );
            setStaff(staffOnly);
        } catch {
            setStaffError('Failed to load staff roster. Please try again.');
        } finally {
            setStaffLoading(false);
        }
    }, []);

    useEffect(() => {
        loadStaff();
    }, [loadStaff]);

    // ── Search for user to promote ────────────────────────────────────────────
    const handlePromoteSearch = async () => {
        if (!promoteQuery.trim() || promoteQuery.trim().length < 2) {
            setPromoteSearchError('Enter at least 2 characters');
            return;
        }
        if (!promoteReason.trim() || promoteReason.trim().length < 10) {
            setPromoteSearchError('Reason must be at least 10 characters');
            return;
        }
        setPromoteSearchError('');
        setPromoteSearchLoading(true);
        setPromoteResults([]);
        setHasPromoteSearched(true);

        try {
            const res = await api.post('/admin/users/search', {
                query: promoteQuery.trim(),
                reason: promoteReason.trim(),
            });
            const data = await res.json();
            if (!res.ok) {
                setPromoteSearchError(data.error || 'Search failed');
                return;
            }
            // Exclude users already staff
            const staffIds = new Set(staff.map(s => s.userId));
            setPromoteResults(
                (data.users || []).filter((u: any) => !staffIds.has(u.userId))
            );
        } catch {
            setPromoteSearchError('Search failed. Please try again.');
        } finally {
            setPromoteSearchLoading(false);
        }
    };

    // ── Open edit dialog ──────────────────────────────────────────────────────
    const openEditDialog = (user: StaffMember | SearchResult, defaultRole?: string) => {
        setEditTarget(user);
        setEditRole(defaultRole || (user as StaffMember).role || 'admin');
        setEditReason('');
        setEditError('');
        setEditSuccess('');
    };

    // ── Submit role change ────────────────────────────────────────────────────
    const handleEditRole = async () => {
        if (!editTarget) return;
        if (!editReason.trim() || editReason.trim().length < 10) {
            setEditError('Reason must be at least 10 characters');
            return;
        }
        setEditLoading(true);
        setEditError('');

        try {
            const res = await api.put(`/admin/users/${editTarget.userId}`, {
                role: editRole,
                reason: editReason.trim(),
            });
            const data = await res.json();
            if (!res.ok) {
                setEditError(data.error || 'Role update failed');
                return;
            }
            setEditSuccess(`Role updated to ${roleLabel(editRole)}`);
            setEditTarget(null);
            setPromoteResults([]);
            setHasPromoteSearched(false);
            setPromoteQuery('');
            setPromoteReason('');
            await loadStaff();
        } catch {
            setEditError('Role update failed. Please try again.');
        } finally {
            setEditLoading(false);
        }
    };

    const closeEditDialog = () => {
        setEditTarget(null);
        setEditReason('');
        setEditError('');
    };

    // ── Load audit log ────────────────────────────────────────────────────────
    const loadAuditLog = async () => {
        if (auditLog.length > 0) {
            setShowAudit(v => !v);
            return;
        }
        setAuditLoading(true);
        setAuditError('');
        try {
            // Use the current admin's own user record as the id —
            // the activity endpoint returns MY recent actions.
            // We use a known super_admin user id from the staff roster.
            const superAdmin = staff.find(s => s.role === 'super_admin');
            if (!superAdmin) {
                setAuditError('Could not load audit log: no super_admin found in roster.');
                return;
            }
            const res = await api.get(`/admin/users/${superAdmin.userId}/activity`);
            const data = await res.json();
            if (!res.ok) {
                setAuditError(data.error || 'Failed to load audit log');
                return;
            }
            setAuditLog(data.myRecentActions || []);
            setShowAudit(true);
        } catch {
            setAuditError('Failed to load audit log.');
        } finally {
            setAuditLoading(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
                Staff & Permissions (RBAC)
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
                Staff management requires super_admin. All role changes are reason-gated and audit-logged.
            </Alert>

            {editSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setEditSuccess('')}>
                    {editSuccess}
                </Alert>
            )}

            {/* ── Staff Roster ───────────────────────────────────────────────── */}
            <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AdminPanelSettings sx={{ color: TEAL }} />
                            <Typography variant="subtitle1" fontWeight={600}>
                                Staff Roster ({staff.length})
                            </Typography>
                        </Box>
                        <Button
                            size="small"
                            onClick={loadStaff}
                            disabled={staffLoading}
                            sx={{ color: TEAL }}
                        >
                            {staffLoading ? <CircularProgress size={14} /> : 'Refresh'}
                        </Button>
                    </Box>

                    {staffError && <Alert severity="error" sx={{ mb: 2 }}>{staffError}</Alert>}

                    {staffLoading && !staff.length ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress sx={{ color: TEAL }} />
                        </Box>
                    ) : staff.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No staff members found.</Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Last Login</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {staff.map(member => (
                                    <TableRow key={member.userId} sx={{ '&:hover': { bgcolor: '#f0fafa' } }}>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={500}>
                                                {member.displayName || '—'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                @{member.username}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{member.email}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={roleLabel(member.role)}
                                                size="small"
                                                color={roleBadgeColor(member.role)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={member.isActive ? 'Active' : 'Suspended'}
                                                size="small"
                                                color={member.isActive ? 'success' : 'error'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {member.lastLogin
                                                    ? new Date(member.lastLogin).toLocaleDateString()
                                                    : 'Never'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="Edit role">
                                                <Button
                                                    size="small"
                                                    startIcon={<Edit fontSize="small" />}
                                                    onClick={() => openEditDialog(member)}
                                                    sx={{ color: TEAL, textTransform: 'none' }}
                                                >
                                                    Edit Role
                                                </Button>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* ── Promote User ───────────────────────────────────────────────── */}
            <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <PersonAdd sx={{ color: TEAL }} />
                        <Typography variant="subtitle1" fontWeight={600}>
                            Promote User to Staff
                        </Typography>
                    </Box>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Promoting a user grants admin access to this dashboard. Super admin only.
                    </Alert>

                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        <TextField
                            label="Email, name, or username"
                            value={promoteQuery}
                            onChange={e => setPromoteQuery(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handlePromoteSearch()}
                            size="small"
                            sx={{ flex: 2, minWidth: 200 }}
                        />
                        <TextField
                            label="Reason for access grant (min 10 chars)"
                            value={promoteReason}
                            onChange={e => setPromoteReason(e.target.value)}
                            size="small"
                            sx={{ flex: 3, minWidth: 240 }}
                        />
                        <Button
                            variant="contained"
                            onClick={handlePromoteSearch}
                            disabled={promoteSearchLoading}
                            startIcon={promoteSearchLoading
                                ? <CircularProgress size={16} color="inherit" />
                                : <Search />}
                            sx={{ bgcolor: TEAL, '&:hover': { bgcolor: '#009688' }, whiteSpace: 'nowrap' }}
                        >
                            Search
                        </Button>
                    </Box>

                    {promoteSearchError && (
                        <Alert severity="error" sx={{ mb: 2 }}>{promoteSearchError}</Alert>
                    )}

                    {hasPromoteSearched && !promoteSearchLoading && (
                        promoteResults.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No non-staff users found matching that query.
                            </Typography>
                        ) : (
                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                    {promoteResults.length} result{promoteResults.length > 1 ? 's' : ''} — click Promote to grant access
                                </Typography>
                                {promoteResults.map(user => (
                                    <Box
                                        key={user.userId}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            p: 1.5,
                                            mb: 1,
                                            borderRadius: 2,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            flexWrap: 'wrap',
                                            gap: 1,
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="body2" fontWeight={500}>
                                                {user.displayName || user.username}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {user.email}
                                            </Typography>
                                        </Box>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CheckCircle fontSize="small" />}
                                            onClick={() => openEditDialog(user, 'admin')}
                                            sx={{ borderColor: TEAL, color: TEAL }}
                                        >
                                            Promote
                                        </Button>
                                    </Box>
                                ))}
                            </Box>
                        )
                    )}
                </CardContent>
            </Card>

            {/* ── Audit Log ─────────────────────────────────────────────────── */}
            <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <History sx={{ color: TEAL }} />
                            <Typography variant="subtitle1" fontWeight={600}>
                                Recent Admin Actions
                            </Typography>
                        </Box>
                        <Button
                            size="small"
                            onClick={loadAuditLog}
                            disabled={auditLoading}
                            sx={{ color: TEAL }}
                        >
                            {auditLoading
                                ? <CircularProgress size={14} />
                                : showAudit ? 'Hide' : 'Show'}
                        </Button>
                    </Box>

                    {auditError && <Alert severity="error" sx={{ mt: 2 }}>{auditError}</Alert>}

                    {showAudit && auditLog.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            No recent admin actions found.
                        </Typography>
                    )}

                    {showAudit && auditLog.length > 0 && (
                        <Table size="small" sx={{ mt: 2 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>When</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {auditLog.map((entry, i) => (
                                    <TableRow key={i}>
                                        <TableCell>
                                            <Chip
                                                label={entry.action_type}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{entry.reason}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {new Date(entry.performed_at).toLocaleString()}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* ── RBAC Reference ─────────────────────────────────────────────── */}
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Security sx={{ color: TEAL }} />
                        <Typography variant="subtitle1" fontWeight={600}>
                            Permission Matrix
                        </Typography>
                        <Tooltip title="Read-only reference. Role assignments above control access.">
                            <Info fontSize="small" sx={{ color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                    </Box>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600 }}>User</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600 }}>Admin</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600 }}>Super Admin</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {PERMISSIONS.map((p, i) => (
                                <TableRow key={i} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                                    <TableCell>
                                        <Typography variant="body2">{p.action}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        {p.user
                                            ? <CheckCircle fontSize="small" sx={{ color: TEAL }} />
                                            : <Typography variant="body2" color="text.disabled">—</Typography>}
                                    </TableCell>
                                    <TableCell align="center">
                                        {p.admin
                                            ? <CheckCircle fontSize="small" sx={{ color: TEAL }} />
                                            : <Typography variant="body2" color="text.disabled">—</Typography>}
                                    </TableCell>
                                    <TableCell align="center">
                                        {p.superAdmin
                                            ? <CheckCircle fontSize="small" sx={{ color: TEAL }} />
                                            : <Typography variant="body2" color="text.disabled">—</Typography>}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* ── Edit / Promote Dialog ─────────────────────────────────────── */}
            <Dialog open={editTarget !== null} onClose={closeEditDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>
                    {editTarget && (editTarget as StaffMember).role
                        ? 'Edit Staff Role'
                        : 'Promote User to Staff'}
                </DialogTitle>
                <DialogContent>
                    {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}

                    {editTarget && (
                        <Box sx={{ mb: 2, mt: 1 }}>
                            <Typography variant="body2" fontWeight={500}>
                                {(editTarget as any).displayName || (editTarget as any).username}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {(editTarget as any).email}
                            </Typography>
                        </Box>
                    )}

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>New Role</InputLabel>
                        <Select
                            value={editRole}
                            onChange={e => setEditRole(e.target.value)}
                            label="New Role"
                        >
                            <MenuItem value="user">User (remove staff access)</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                            <MenuItem value="super_admin">Super Admin</MenuItem>
                        </Select>
                    </FormControl>

                    {editRole === 'user' && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            This will remove dashboard access for this person.
                        </Alert>
                    )}
                    {editRole === 'super_admin' && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            Super admin has full platform access including user deletion and role management.
                        </Alert>
                    )}

                    <TextField
                        fullWidth
                        label="Reason (min 10 chars) — audit logged"
                        value={editReason}
                        onChange={e => setEditReason(e.target.value)}
                        multiline
                        rows={2}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={closeEditDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleEditRole}
                        disabled={editLoading || editReason.trim().length < 10}
                        sx={{ bgcolor: TEAL, '&:hover': { bgcolor: '#009688' } }}
                    >
                        {editLoading ? <CircularProgress size={20} color="inherit" /> : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default StaffPermissionsTab;

